import { useCallback, useMemo, useState } from 'react';

import {
  useGetProductsRegistryListsQuery,
  useSearchProductsMutation,
} from '@app/api/Products';
import type {
  ModelProduct,
  ModelProductsSearchFilter,
} from '@app/api/Products';
import { useUserPrint } from '@app/hooks';
import {
  getProductDirectoryColumns,
  initialFilters,
} from '@app/pages/Warehouses/ProductDirectory/data';
import type { FilterChangeType } from '@app/pages/Warehouses/ProductDirectory/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { PaginationProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import type { ChangeEventHandler } from 'react';

export const useProductDirectory = () => {
  const { showNotification } = useNotification();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const [tableData, setTableData] = useState<ModelProduct[]>([]);
  const [total, setTotal] = useState<number>(0);

  const [unsavedFilters, setUnsavedFilters] =
    useState<ModelProductsSearchFilter>(() => initialFilters);

  const [filters, setFilters] = useState<ModelProductsSearchFilter>(
    () => initialFilters
  );
  const {
    data: filtersLists,
    isFetching: isFiltersListsFetching,
    isLoading: isFiltersListsLoading,
    isUninitialized: isFiltersListsUninitialized,
  } = useGetProductsRegistryListsQuery(
    { ActiveBranchId },
    { skip: !ActiveBranchId, refetchOnMountOrArgChange: true }
  );
  const [
    searchProducts,
    {
      isLoading: isSearchProductsLoading,
      isUninitialized: isSearchProductsUninitialized,
    },
  ] = useSearchProductsMutation();

  const onFilterChange: FilterChangeType = useCallback((key, value) => {
    setUnsavedFilters((prev) => ({
      ...prev,
      [key]: value.map(({ value }) => value),
    }));
  }, []);

  const onChangeSearchTerm: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value: SearchTerm } }) => {
      setUnsavedFilters((prev) => ({ ...prev, SearchTerm }));
    },
    []
  );

  const skipFilters = useCallback(() => {
    setUnsavedFilters(initialFilters);
    setFilters(initialFilters);
  }, []);

  const onSearch = useCallback(() => {
    setFilters({
      ...unsavedFilters,
      PageN: 1,
    });
  }, [unsavedFilters]);

  const searchProductsHandler = useCallback(async () => {
    try {
      const response = await searchProducts({
        ...filters,
        ActiveBranchId,
      }).unwrap();
      const { CountProducts, Products } = response;
      const sortedProducts = Products
        ? [...Products].sort((a, b) =>
            a?.IsDeleted === b?.IsDeleted ? 0 : a?.IsDeleted ? 1 : -1
          )
        : [];
      setTableData(sortedProducts ?? []);
      setTotal(CountProducts ?? 0);
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [showNotification, filters, searchProducts, ActiveBranchId]);

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (PageN: number) => {
      const updatedFilters = { ...filters, PageN };
      setFilters(updatedFilters);
      setUnsavedFilters(updatedFilters);
    },
    [filters]
  );

  const onPageSizeChange = useCallback(
    (ItemsPerPage: number) => {
      const updatedFilters = { ...filters, ItemsPerPage, PageN: 1 };
      setFilters(updatedFilters);
      setUnsavedFilters(updatedFilters);
    },
    [filters]
  );

  const categoryOptions = useMemo(() => {
    if (!filtersLists) return [];

    const { Categories } = filtersLists;

    const categoryOptions: DefaultOptionType[] = Categories.map(
      ({ Name, Id }) => ({ label: Name, value: Id })
    );

    return categoryOptions;
  }, [filtersLists]);

  const brandsOptions = useMemo(() => {
    if (!filtersLists) return [];

    const { Brands } = filtersLists;

    const brandsOptions: DefaultOptionType[] = Brands.map(({ Name, Id }) => ({
      label: Name,
      value: Id,
    }));

    return brandsOptions;
  }, [filtersLists]);

  const isPageLoading = useMemo(() => {
    return isSearchProductsLoading || isSearchProductsUninitialized;
  }, [isSearchProductsLoading, isSearchProductsUninitialized]);

  const isFetching = useMemo(() => {
    return isSearchProductsLoading;
  }, [isSearchProductsLoading]);

  const disabledFilters = useMemo(() => {
    return (
      isSearchProductsLoading ||
      isSearchProductsUninitialized ||
      isFiltersListsFetching ||
      isFiltersListsLoading ||
      isFiltersListsUninitialized
    );
  }, [
    isSearchProductsLoading,
    isSearchProductsUninitialized,
    isFiltersListsFetching,
    isFiltersListsLoading,
    isFiltersListsUninitialized,
  ]);

  const columns = useMemo(
    () => getProductDirectoryColumns({ filtersLists }),
    [filtersLists]
  );

  return {
    onChangeSearchTerm,
    unsavedFilters,
    tableData,
    total,
    isPageLoading,
    isFetching,
    searchProductsHandler,
    onFilterChange,
    skipFilters,
    onSearch,
    onPaginate,
    onPageSizeChange,
    disabledFilters,
    categoryOptions,
    brandsOptions,
    filters,
    columns,
  };
};
